// ────────────────────────────────────────────────────────────────────
// CommandSearch — the "found in 5 seconds" box (⌘/Ctrl-K).
// One field over the whole tenant: type a tag, CML, plant or service and
// jump straight there. Backed by GET /api/search (RLS-scoped server-side).
// ────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { useIsNarrow } from "@/lib/useIsNarrow";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import type { SearchHit } from "@/lib/types";

const TYPE_META: Record<string, { glyph: string; label: string }> = {
  plant:      { glyph: "▣", label: "PLANT" },
  asset:      { glyph: "⬡", label: "ASSET" },
  cml:        { glyph: "◈", label: "CML" },
  inspection: { glyph: "▤", label: "REPORT" },
  file:       { glyph: "▤", label: "DOCUMENT" },
  package:    { glyph: "▦", label: "PACKAGE" },
  asset_docs: { glyph: "▤", label: "DOCUMENTS" },
};
// A hit type this build has never heard of still has to render. The palette is
// the one surface where a backend that ran ahead of the frontend must degrade,
// not disappear.
const UNKNOWN_META = { glyph: "·", label: "" };


/** `inline` renders the header's own search field (the handoff's 240px box)
 *  instead of the button-plus-overlay. Same state, same debounce, same
 *  apiClient.search, same `go` — only the chrome differs, so there is one
 *  search in this app and not two.
 *
 *  Below 1280px the field would be squeezed to 109-224px and the header row
 *  stopped fitting at all (measured). There it collapses to an icon button that
 *  opens THIS COMPONENT'S existing overlay — the non-inline path, which until
 *  now had no production caller and was ~55 lines kept alive by its own test.
 *  One change recovers the field's whole width in the band where it is the
 *  problem and gives the overlay a real consumer again.
 *
 *  ⌘K focuses the field where the field exists, and opens the overlay where it
 *  does not. */
export function CommandSearch({ inline = false }: { inline?: boolean } = {}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  // 1280 is the first width at which the row fits with the field at its full
  // 240px (measured). Below it the field becomes a button — at 1240 the field
  // still rendered but the nav clipped 2px, so the boundary sits here.

  const compact = useIsNarrow(1280);
  const asField = inline && !compact;
  // The overlay markup is the non-inline path's, reused verbatim by the compact
  // trigger below — the point of collapsing to a button is to have ONE search,
  // not a third surface.
  // ⌘K / Ctrl-K opens (or, inline, focuses); Esc closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (asField) inputRef.current?.focus();
        else setOpen((v) => !v);
      } else if (e.key === "Escape") {
        if (asField) { setHits([]); setQ(""); }
        else setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [asField]);

  useEffect(() => {
    if (open) { setQ(""); setHits([]); setActive(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  // Debounced query.
  useEffect(() => {
    if (!open && !asField) return;
    const term = q.trim();
    if (!term) { setHits([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      apiClient.search(term)
        .then((r) => { setHits(r.results); setActive(0); })
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, 160);
    return () => clearTimeout(t);
  }, [q, open, asField]);

  function go(hit: SearchHit): void {
    setOpen(false);
    setQ("");
    setHits([]);
    navigate(hit.href);
  }

  function onInputKey(e: React.KeyboardEvent): void {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, hits.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && hits[active]) { e.preventDefault(); go(hits[active]); }
  }
  const overlay = open && (
        <div className="fixed inset-0 z-[2000] flex items-start justify-center"
             style={{ background: "rgba(6,18,31,0.5)", backdropFilter: "blur(3px)", paddingTop: "12vh" }}
             onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="ค้นหาทั่วระบบ"
               style={{ width: "min(600px, 92vw)", background: "var(--paper)", borderRadius: 14,
                        border: "1px solid var(--hairline)", overflow: "hidden",
                        boxShadow: "0 30px 80px -20px rgba(0,0,0,.6)" }}>
            <div className="flex items-center gap-3 px-4" style={{ borderBottom: "1px solid var(--hairline)", height: 54 }}>
              <SearchIcon />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
                     placeholder="tag · CML · plant · service — เช่น V-101, CML-7"
                     className="flex-1 font-mono" autoComplete="off" spellCheck={false}
                     style={{ background: "transparent", border: 0, outline: "none", fontSize: 15,
                              color: "var(--ink-1)", letterSpacing: ".01em" }} />
              {loading && <Spinner />}
            </div>

            <div style={{ maxHeight: "56vh", overflowY: "auto" }}>
              {q.trim() && !loading && hits.length === 0 && (
                <div className="px-4 py-6 font-mono" style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                  ไม่พบ “{q.trim()}” — ลอง tag, CML no., ชื่อ plant หรือ service
                </div>
              )}
              {hits.map((h, i) => (
                <HitRow key={`${h.type}-${h.id}`} hit={h} active={i === active}
                        onSelect={() => go(h)} onHover={() => setActive(i)} />
              ))}
            </div>

            <div className="flex items-center gap-4 px-4 font-mono"
                 style={{ height: 34, borderTop: "1px solid var(--hairline)", fontSize: 10,
                          letterSpacing: ".1em", color: "var(--ink-4)" }}>
              <span>↑↓ เลือก</span><span>↵ ไป</span><span>esc ปิด</span>
            </div>
          </div>
        </div>
  );

  if (asField) {
    // THE WRAPPER is the flex item, so the flex properties belong here. They
    // were on the inner box, whose parent is this block container — flex-shrink
    // on a block child is inert, and `width: 240` was then a hard floor: the box
    // held 240px while this wrapper shrank under it and overflowed onto the bell
    // and the account menu. Measured before the fix: wrapper 73.7px, box 240px.
    //
    // This field only renders at >= 1280 (see `compact` above); below that the
    // button branch replaces it, so a width table for 1240 and under would
    // describe something that no longer exists.
    //
    // Measured in Chromium against the built CSS. Field is 240px at every width
    // it renders at (1280 through 1920). PAGE overflow is 0px at 900, 960, 1024,
    // 1152, and 1240 through 1920 — the invariant is that the page never gains
    // a horizontal scrollbar at >= 900px. The nav may scroll inside its own
    // overflow-x-auto instead, and does give 16px at 900.
    //
    // jsdom computes no layout, so none of this can be asserted in the suite.
    // What the suite pins is the structural chain these numbers depend on.
    return (
      <div className="relative min-w-0" style={{ width: 240, flexShrink: 1, minWidth: 92 }}>
        <div className="flex items-center gap-2 px-2.5 min-w-0"
             style={{ width: "100%", height: 26, borderRadius: 7,
                      background: "var(--bg-dark)",
                      // 3:1 is the non-text threshold and --ink-2 gave 1.78:1
                      // against the header, over a fill that is itself 1.19:1 —
                      // the field had no visible boundary. --ink-4 is 7.2:1.
                      border: "1px solid var(--ink-4)" }}>
          <SearchIcon tone="var(--ink-4)" />
          <input ref={inputRef} type="search" value={q} aria-label="ค้นหา (Ctrl+K)"
                 onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
                 placeholder="tag · CML · plant"
                 className="flex-1 min-w-0 font-mono" autoComplete="off" spellCheck={false}
                 style={{ background: "transparent", border: 0, outline: "none", fontSize: 11.5,
                          color: "var(--paper)", letterSpacing: ".02em" }} />
          {loading && <Spinner />}
        </div>
        {/* The overlay this field replaced said "ไม่พบ …" when a term matched
            nothing. Without it the header field answers a real search with a
            spinner and then silence, which is what a broken field looks like. */}
        {q.trim() !== "" && !loading && (
          <div className="absolute right-0 z-[1100] w-[420px] overflow-hidden"
               style={{ top: 32, background: "var(--paper-2)", border: "1px solid var(--hairline)",
                        borderRadius: 10, boxShadow: "0 18px 44px -18px rgba(10,22,40,.45)" }}>
            {hits.length === 0 ? (
              <div className="px-4 py-4 font-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
                ไม่พบ “{q.trim()}” — ลอง tag, CML no., ชื่อ plant หรือ service
              </div>
            ) : hits.map((h, i) => (
              <HitRow key={`${h.type}-${h.id}`} hit={h} active={i === active}
                      onSelect={() => go(h)} onHover={() => setActive(i)} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (inline) {
    // Compact: below 1280px the field becomes this button and the overlay does
    // the searching. Same aria-label and same ⌘K, so the control the user
    // reaches for is the same one, at whatever width.
    return (
      <>
        <button onClick={() => setOpen(true)} aria-label="ค้นหา (Ctrl+K)"
                className="shrink-0 flex items-center justify-center transition-colors"
                style={{ width: 26, height: 26, borderRadius: 7,
                         background: "var(--bg-dark)", border: "1px solid var(--ink-4)" }}>
          <SearchIcon tone="var(--ink-4)" />
        </button>
        {overlay}
      </>
    );
  }

  return (
    <>
      {/* trigger in the header */}
      <button onClick={() => setOpen(true)}
              className="flex items-center gap-2 h-9 pl-3 pr-2.5 rounded-md transition-colors font-mono"
              style={{ fontSize: 12, color: "var(--ink-3)", border: "1px solid var(--hairline)",
                       background: "rgba(10,22,40,0.02)" }}
              aria-label="ค้นหา (Ctrl+K)">
        <SearchIcon />
        <span className="hidden sm:inline" style={{ letterSpacing: ".04em" }}>Search…</span>
        <kbd className="hidden sm:inline" style={{ fontFamily: "var(--mono)", fontSize: 10,
             padding: "1px 5px", borderRadius: 4, border: "1px solid var(--hairline)", color: "var(--ink-4)" }}>⌘K</kbd>
      </button>
      {overlay}
    </>
  );
}

// `tone` because this icon is reused on the dark header field, where the
// light-overlay --ink-3 falls to 2.16:1 against --bg-dark.
function SearchIcon({ tone = "var(--ink-3)" }: { tone?: string } = {}): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{ color: tone, flexShrink: 0 }}>
      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );
}
function Spinner(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ animation: "spin 1s linear infinite", color: "var(--ink-3)" }}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity=".2" strokeWidth="2" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** One result row. Shared by the overlay and the header field so a new hit
 *  type renders identically in both — the defect this component's test was
 *  written for (a missing TYPE_META entry threw on m.glyph) would otherwise
 *  have had two places to come back in. */
function HitRow({ hit, active, onSelect, onHover }: {
  hit: SearchHit; active: boolean; onSelect: () => void; onHover: () => void;
}): JSX.Element {
  const m = TYPE_META[hit.type] ?? UNKNOWN_META;
  return (
    <button onClick={onSelect} onMouseEnter={onHover}
            className="w-full flex items-center gap-3 px-4 text-left transition-colors"
            style={{ height: 52, background: active ? "rgba(var(--accent-rgb) / 0.09)" : "transparent",
                     borderLeft: `2px solid ${active ? "var(--accent)" : "transparent"}` }}>
      <span className="font-mono" style={{ fontSize: 15, color: "var(--accent)", width: 20, textAlign: "center" }}>{m.glyph}</span>
      <span className="flex-1 min-w-0">
        <span className="font-mono block truncate" style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 600 }}>{hit.label}</span>
        <span className="font-mono block truncate" style={{ fontSize: 11, color: "var(--ink-3)" }}>{hit.sublabel}</span>
      </span>
      <span className="font-mono" style={{ fontSize: 9, letterSpacing: ".16em", color: "var(--ink-4)",
            border: "1px solid var(--hairline)", borderRadius: 5, padding: "2px 6px" }}>{m.label}</span>
    </button>
  );
}
